import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Header,
  Segment,
  Form,
  Button,
  Message,
  Icon,
  Dropdown,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders, getCurrentUserId } from '../../api';

export default function ContributorApply() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [languages, setLanguages] = useState([]);
  const [motivation, setMotivation] = useState('');
  const [experience, setExperience] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState(null); // { id, fullname, email }
  const [alreadyContributor, setAlreadyContributor] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    async function fetchLanguages() {
      try {
        const res = await fetch(`${getApiPath()}/@polyglot/coverage-summary`);
        if (res.ok) {
          const data = await res.json();
          const langs = (data.languages || []).sort();
          setAvailableLanguages(langs.map((l) => ({ key: l, value: l, text: l })));
        }
      } catch {
        // Endpoint unavailable — dropdown will be empty, user sees validation error
      }
    }
    fetchLanguages();

    // Check if user is logged in — pre-fill name/email
    const userId = getCurrentUserId();
    if (userId) {
      fetch(`${getApiPath()}/@polyglot/contributors/me`, {
        headers: getAuthHeaders(),
      })
        .then((res) => {
          if (res.ok) {
            // Already a contributor — redirect to dashboard
            setAlreadyContributor(true);
          } else if (res.status === 404) {
            // Logged in but not a contributor — fetch user info
            return fetch(`${getApiPath()}/@users/${userId}`, {
              headers: getAuthHeaders(),
            });
          }
        })
        .then((res) => {
          if (res && res.ok) {
            return res.json();
          }
        })
        .then((data) => {
          if (data) {
            setLoggedInUser({ id: userId, fullname: data.fullname, email: data.email });
            setName(data.fullname || '');
            setEmail(data.email || '');
          }
        })
        .catch(() => {
          // Not logged in or fetch failed — show full form
        });
    }
  }, []);

  const handleSubmit = async () => {
    setError(null);
    if (!name || name.trim().length < 2) { setError('Please enter your name.'); return; }
    if (!email || !email.includes('@')) { setError('Please enter a valid email.'); return; }
    if (languages.length === 0) { setError('Please select at least one language.'); return; }

    // CV or motivation required — at least one must be provided
    if (!motivation?.trim() && !cvFile) {
      setError('Please tell us briefly why you want to contribute, or upload a CV.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        languages,
        motivation: motivation?.trim() || '',
        experience: experience?.trim() || null,
      };

      // Encode CV as base64 (Plone-style file upload)
      if (cvFile) {
        const buffer = await cvFile.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(buffer).reduce((s, b) => s + String.fromCharCode(b), ''),
        );
        payload.cv = {
          data: base64,
          encoding: 'base64',
          'content-type': cvFile.type || 'application/pdf',
          filename: cvFile.name,
        };
      }

      const res = await fetch(`${getApiPath()}/@polyglot/contributors/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Submission failed. Please try again.');
        setSubmitting(false);
        return;
      }

      setSuccess(true);
    } catch (err) {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  if (alreadyContributor) {
    return (
      <Container style={{ paddingTop: '2em', maxWidth: '700px' }}>
        <Helmet title="Already a Contributor" />
        <Message info icon>
          <Icon name="check circle" />
          <Message.Content>
            <Message.Header>You're already a contributor</Message.Header>
            <a href="/polyglot/dashboard">Go to your dashboard</a>
          </Message.Content>
        </Message>
      </Container>
    );
  }

  if (success) {
    return (
      <Container style={{ paddingTop: '2em', maxWidth: '700px' }}>
        <Helmet title="Application Received" />
        <Message success icon>
          <Icon name="check circle" />
          <Message.Content>
            <Message.Header>Application Received</Message.Header>
            Thank you for your interest in contributing! The project administrator
            will review your application and contact you if there is a match.
          </Message.Content>
        </Message>
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '2em', maxWidth: '700px' }}>
      <Helmet title="Apply to Contribute" />
      <Header as="h1">
        <Icon name="translate" />
        <Header.Content>
          Help Translate the Plone Ecosystem
          <Header.Subheader>
            Become an Official Polyglot Translator
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <p>
          The Polyglot Translation Project manages translations for Plone, Volto, and Nick CMS
          across <strong>71 languages</strong> — 330,000+ translations used in production across Europe.
          We're looking for native speakers and experienced translators to review and improve
          translation quality.
        </p>
      </Segment>

      <Segment>
        <Header as="h4">What contributors do</Header>
        <p>
          <Icon name="check" color="green" /> <strong>Review</strong> existing translations —
          most of the work is reading, judging, and confirming.
          Vote "looks good" or flag what needs fixing.
        </p>
        <p>
          <Icon name="pencil" color="blue" /> <strong>Correct</strong> translations that are wrong,
          awkward, or unnatural in your language.
        </p>
        <p>
          <Icon name="comment" color="teal" /> <strong>Comment</strong> with context that helps the
          next reviewer — "this term should match the Settings page" or "too formal for a button label."
        </p>
      </Segment>

      <Segment>
        <Header as="h4">What you get</Header>
        <p>
          <Icon name="certificate" color="yellow" /> <strong>Official Polyglot Translator certificate</strong> —
          personalised, shareable, verifiable. Something to put on your CV or LinkedIn.
        </p>
        <p>
          <Icon name="user" color="blue" /> <strong>Your name on your work</strong> — every review
          is a formal attestation. Your expertise is recorded and respected.
        </p>
        <p>
          <Icon name="creative commons" color="green" /> <strong>Public domain contribution</strong> (CC0) —
          your work goes into production software used across Europe, free for everyone forever.
          You are <strong>held harmless</strong> for translations used within the platform.
        </p>
      </Segment>

      <Segment>
        <Header as="h4">What we ask</Header>
        <p>
          Native speaker or professional-level competence in at least one of our 71 languages.
          Willingness to complete a short onboarding (~10 minutes) and accept the{' '}
          <a href="/policies/AGR-TRN-001">Contributors Agreement</a>. Your honest best effort.
        </p>
        <p style={{ color: '#888', fontSize: '0.9em' }}>
          Fill in the form below and the project administrator will review your application.
          If approved, you will receive an invitation with a registration link.
        </p>
      </Segment>

      {error && <Message error>{error}</Message>}

      <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <Segment>
          <Header as="h3">About You</Header>
          {loggedInUser && (
            <Message info size="small">
              <Icon name="user" /> Logged in as <strong>{loggedInUser.id}</strong>
            </Message>
          )}
          <Form.Input
            label="Full Name"
            placeholder="Your full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            readOnly={!!loggedInUser}
            required
          />
          <Form.Input
            label="Email"
            placeholder="your@email.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            readOnly={!!loggedInUser}
            required
          />
        </Segment>

        <Segment>
          <Header as="h3">Languages</Header>
          <Form.Field required>
            <label>Which languages can you help with?</label>
            <Dropdown
              placeholder="Select languages"
              multiple
              search
              selection
              options={availableLanguages}
              value={languages}
              onChange={(e, { value }) => setLanguages(value)}
            />
          </Form.Field>
        </Segment>

        <Segment>
          <Header as="h3">About Your Background</Header>
          <Form.TextArea
            label="Why do you want to contribute? (optional if CV uploaded)"
            placeholder="A sentence or two is fine — native speaker? Plone community member? Professional translator?"
            value={motivation}
            onChange={(e) => setMotivation(e.target.value)}
            rows={3}
          />
          <Form.TextArea
            label="Professional experience (optional)"
            placeholder="Relevant work experience, certifications, or portfolio links"
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
            rows={3}
          />
          <Form.Field>
            <label>Upload document (optional — CV, certificates, diplomas. PDF/JPG/PNG, max 5 MB)</label>
            <Button
              basic
              icon="attach"
              content={cvFile ? cvFile.name : 'Choose file'}
              onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}
              color={cvFile ? 'green' : undefined}
            />
            {cvFile && (
              <Button
                basic
                icon="delete"
                size="tiny"
                onClick={(e) => { e.preventDefault(); setCvFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                style={{ marginLeft: '0.5em' }}
              />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && file.size > 5 * 1024 * 1024) {
                  setError('CV file must be under 5 MB.');
                  return;
                }
                setCvFile(file || null);
              }}
            />
          </Form.Field>
        </Segment>

        <Button
          primary
          size="large"
          fluid
          type="submit"
          loading={submitting}
        >
          <Icon name="send" />
          Submit Application
        </Button>
      </Form>
    </Container>
  );
}
